/**
 * @file   src\components\OrderPayment.tsx
 * @brief  OrderPayment component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, Button,  useNavigate, useState } from '../utils/thirdpartyComponents';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Row, Col } from '../utils/thirdpartyComponents';
import CWInput from './CWInput';
import {  NAME_MAX_LENGTH } from '../utils/constants';
import { formatCreditCardNumber, formatExpirationDate, getFromLocalStorage } from '../utils/helper';
import * as Yup from 'yup';
import {  OrderHardwareApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { MessageToaster } from '../utils/toastUtil';
import { pageURLs } from '../utils/pageUrls';
import SmallLoader from './SmallLoader';

const OrderPayment = ({ onPaymentComplete, cartData }: { onPaymentComplete: (isValid: boolean) => void; cartData: any }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate()
  // State for form values
  const [formValues, setFormValues] = useState({
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [cardErrors, setCardErrors] = useState<{ custcardnumber?: string; custexpirydate?: string; custcvv?: string, custcardname?: string }>({});

  // Instantiate MessageToaster
  const toaster = new MessageToaster();
  // State for validation errors


  const cardTypeOption = {
    showIcon: true,
  };

  // Handle input change and remove validation errors on change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // To allow only digits in cardnumber, cvv field
    if ((e.target.name === "cardNumber")) {
      // Extract only digits from the input value
      const cardnumber_value = e.target.value;
      if (!/^\d*$/.test(cardnumber_value)) {
        e.target.value = cardnumber_value.replace(/\D/g, '');
      }

      const formattedValue = formatCreditCardNumber(e.target.value);
      setFormValues({
        ...formValues,
        [name]: formattedValue,
      });
    } else if ((e.target.name === "cvv")) {

      const new_value = e.target.value;
      const regex = /^(0|[0-9][0-9]{0,9})?$/
      if (regex.test(new_value)) {
        setFormValues({
          ...formValues,
          [name]: new_value,
        });
      }
    } else if ((e.target.name === "expiryDate")) {
      // Extract only digits from the input value
      const expdate_value = e.target.value;
      if (!/^\d*$/.test(expdate_value)) {
        e.target.value = expdate_value.replace(/\D/g, '');
      }

      const formattedValue = formatExpirationDate(e.target.value);
      setFormValues({
        ...formValues,
        [name]: formattedValue,
      });

    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });

    }

  };

  const handlePaymentSubmit = async (event: React.FormEvent) => {
    try {

      event.preventDefault();


      if (!stripe || !elements) {
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);
      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        return;
      }
      setIsSubmitted(true)
      setLoading(true);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: formValues.cardName,
        }
      });


      if (error) {
        setIsSubmitted(false)
        setLoading(false)
        return;
      }
      else {
        purchaseHardware(paymentMethod?.id)
      }



    } catch (error) {
      setLoading(false);
      //If validation fails on submit, then errors are shown
      if (error instanceof Yup.ValidationError) {

        const newErrors: any = {};
        error.inner.forEach((err) => {
          newErrors[err.path as keyof typeof formValues] = err.message;
        });
        // setErrors(newErrors);
        setCardErrors(newErrors)
      }
    }
  }


  const purchaseHardware = async (id: string) => {

    const devices = cartData.devices
    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const deviceData = Object.keys(devices).map((key) => ({
      itemId: devices[key].hardwareId,  // Adjust starting itemId if necessary
      quantity: devices[key].quantity
    }));

    const requestParam = {
      "carWashId": userInfo.carWashId,
      "hardwareItems": deviceData,
      "packageItems":
      {
        "itemId": cartData.packageItems ? cartData.packageItems?.packageId : 0
      },
      "initialFee": parseFloat(cartData.initialFee.toFixed(2)),
      "grossAmount": parseFloat((cartData.grandTotal + cartData.initialFee).toFixed(2)),
      "shipping": cartData.shippingAddress,
      "billing": cartData.billingAddress,
      "sameBilling": 0,
      "paymentMethod": id
    }
    try {
      setLoading(true); // Show loader
      const responseHardwarePurchase =
        await axios.post(OrderHardwareApi.CARWASH_OWNER_PURCHASE_HARDWARE, requestParam)
        ;
      if (responseHardwarePurchase?.data?.error === 0 && responseHardwarePurchase?.data?.data?.paymentStatus == 1) {
        navigate("/"+pageURLs.payment_success,{state:{orderId:responseHardwarePurchase?.data?.data?.orderId}})
      }
      else {
        toaster.toastError(responseHardwarePurchase?.data?.message);
      }
    } catch{
      toaster.toastError('Failed to purchase hardware.');
      setLoading(false); 
    } finally {
      setLoading(false); // Hide loader
      setIsSubmitted(false)
    }


  }

  const handleCardNumberChange = (event: any) => {
    setCardErrors((prevErrors) => ({ ...prevErrors, custcardnumber: event.error ? event.error.message : '' }));

  };

  const handleCardExpiryChange = (event: any) => {

    setCardErrors((prevErrors) => ({ ...prevErrors, custexpirydate: event.error ? event.error.message : '' }));
  };

  const handleCardCvcChange = (event: any) => {

    setCardErrors((prevErrors) => ({ ...prevErrors, custcvv: event.error ? event.error.message : '' }));
  };




  // Returning payment form
  return (
    <div className='order-payment-wrap mx-auto'>
      <div className="payment-amount-block">
        <div className="inner-wrap">
          <div className="total-group price-info d-flex align-items-end flex-column">
            <div className="price d-flex align-items-center">
              <p className='f-bold'>Total:</p>
              <h3 className='f-bold'>${(cartData.grandTotal).toFixed(2)}</h3>
            </div>
           { cartData.initialFee>0 && <div className="price d-flex align-items-center">
              <p className='f-bold'>Initial Fee:</p>
              <h3 className='f-bold'>${(cartData.initialFee).toFixed(2)}</h3>
            </div>}
          </div>
          <div className="price-info">
            <div className="price d-flex align-items-center justify-content-end">
              <p className='f-bold'>Grand Total:</p>
              <h3 className='f-bold total'>${(cartData.grandTotal + cartData.initialFee).toFixed(2)}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="payment-form">
        <CWInput
          id="cardname"
          name="cardName"
          type="text"
          label="Name on Card"
          value={formValues.cardName}
          onChange={handleInputChange}
          maxLength={NAME_MAX_LENGTH}
        />

        <div className="form-group cw-input">
          <div className="cust-label-group">
            <label>Card Number</label>
            <span className="mandatory">*</span>
            <CardNumberElement className="form-control" onChange={handleCardNumberChange} options={cardTypeOption} />
            {cardErrors.custcardnumber && <span className="error-msg">{cardErrors.custcardnumber}</span>}
          </div>
        </div>
        {/* <div className="multi-field"> */}
        <Row>
          <Col lg={6}>
            <div className="form-group cw-input">
              <div className="cust-label-group">
                <label>Expiry Date</label>
                <span className="mandatory">*</span>
                <CardExpiryElement className="form-control" onChange={handleCardExpiryChange} />
                {cardErrors.custexpirydate && <span className="error-msg">{cardErrors.custexpirydate}</span>}
              </div>
            </div>
          </Col>
          <Col lg={6}>
            <div className="form-group cw-input">
              <div className="cust-label-group">
                <label>CVC</label>
                <span className="mandatory">*</span>
                <CardCvcElement className="form-control" onChange={handleCardCvcChange} />
                {cardErrors.custcvv && <span className="error-msg">{cardErrors.custcvv}</span>}
              </div>
            </div>
          </Col>
        </Row>
        <div className="btn-sec text-end">
        <Button variant="primary" className="primary-btn btn-main text-center" onClick={handlePaymentSubmit} disabled={isSubmitted} title='Pay'>Pay</Button>
        </div>
      </div>
      {loading && <SmallLoader />}
    </div>
  );
};

export default OrderPayment;